import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import type { Category, Product } from '@bazaar/shared';
import { api, ApiError } from '../../lib/api.js';
import { useToast } from '../../context/ToastContext.js';
import { mergeCategoryProductCounts } from '../../lib/categoryCounts.js';
import { BigButton } from '../../components/BigButton.js';
import { Spinner } from '../../components/Spinner.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Page, PageHeader } from '../../components/layout.js';

const BRAND_COLOURS = [
  { label: 'Teal', value: '#008d90' },
  { label: 'Pink', value: '#e83ebc' },
  { label: 'Orange', value: '#ff8a3d' },
  { label: 'Aqua', value: '#b3e5e3' },
];

export function CategoriesPage() {
  const { showToast } = useToast();
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newName, setNewName] = useState('');
  const [newColour, setNewColour] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editColour, setEditColour] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<Category | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        api.listCategories(),
        api.listProducts({ includeInactive: true }),
      ]);
      setCategories(categoriesRes.categories);
      setProducts(productsRes.products);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const withCounts = mergeCategoryProductCounts(categories, products);

  async function handleCreate() {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const nextSortOrder = categories.length > 0 ? Math.max(...categories.map((c) => c.sortOrder)) + 1 : 0;
      await api.createCategory({ name: newName.trim(), sortOrder: nextSortOrder, colour: newColour });
      setNewName('');
      setNewColour(null);
      await load();
      showToast('Category added', 'success');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not add category', 'error');
    } finally {
      setCreating(false);
    }
  }

  function startEdit(category: Category) {
    setEditingId(category.id);
    setEditName(category.name);
    setEditColour(category.colour);
  }

  async function handleSaveEdit(id: number) {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await api.updateCategory(id, { name: editName.trim(), colour: editColour });
      setEditingId(null);
      await load();
      showToast('Category updated', 'success');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not update category', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleMove(index: number, direction: -1 | 1) {
    const sorted = [...withCounts];
    const other = index + direction;
    if (other < 0 || other >= sorted.length) return;

    // Swap the two positions, then renumber *every* category to its plain
    // index in the new order (0, 1, 2, ...) instead of just swapping the
    // two `sortOrder` values. Two categories can end up with the same
    // sortOrder (stale/seeded data, or a race) and swapping equal values is
    // a silent no-op - renumbering always produces a visible move and also
    // cleans up any pre-existing tie. See the QA report, P3 #19.
    const reordered = [...sorted];
    [reordered[index], reordered[other]] = [reordered[other]!, reordered[index]!];

    try {
      await Promise.all(reordered.map((category, i) => api.updateCategory(category.id, { sortOrder: i })));
      await load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not reorder categories', 'error');
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.deleteCategory(pendingDelete.id);
      setPendingDelete(null);
      await load();
      showToast('Category deleted', 'success');
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : 'Could not delete category', 'error');
    } finally {
      setDeleting(false);
    }
  }

  if (loading) return <Spinner label="Loading categories" />;
  if (error) return <EmptyState title="Could not load categories" description={error} />;

  return (
    <Page>
      <PageHeader title="Categories" />

      <div className="card card-stack">
        <h2 className="section-title">Add a category</h2>
        {/* flex-wrap: the colour picker's row of round swatches plus the
            name input and Add button don't all fit on one line at ~768px
            without it - see QA report P3 #22. */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Category name"
            maxLength={40}
            className="input flex-1 min-w-40"
          />
          <ColourPicker value={newColour} onChange={setNewColour} />
          <BigButton className="shrink-0" disabled={!newName.trim() || creating} onClick={() => void handleCreate()}>
            Add
          </BigButton>
        </div>
      </div>

      {withCounts.length === 0 ? (
        <EmptyState title="No categories yet" description="Add one above to start grouping products." />
      ) : (
        <ul className="list-stack">
          {withCounts.map((category, index) => (
            <li key={category.id} className="card-row flex flex-col gap-2">
              {editingId === category.id ? (
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    maxLength={40}
                    className="input flex-1 min-w-40"
                  />
                  <ColourPicker value={editColour} onChange={setEditColour} />
                  <div className="flex gap-2 shrink-0">
                    <BigButton variant="outline" onClick={() => setEditingId(null)}>
                      Cancel
                    </BigButton>
                    <BigButton disabled={saving} onClick={() => void handleSaveEdit(category.id)}>
                      Save
                    </BigButton>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <span
                      aria-hidden="true"
                      className="h-6 w-6 rounded-full shrink-0 border border-border"
                      style={{ backgroundColor: category.colour ?? 'transparent' }}
                    />
                    <div className="min-w-0">
                      <p className="font-body font-semibold text-base text-text truncate">{category.name}</p>
                      <p className="font-body text-sm text-muted">
                        {category.productCount} product{category.productCount === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                    <button
                      type="button"
                      aria-label={`Move ${category.name} up`}
                      disabled={index === 0}
                      onClick={() => void handleMove(index, -1)}
                      className="h-12 w-12 rounded-lg bg-aqua text-teal-dark font-heading font-bold disabled:opacity-30"
                    >
                      &uarr;
                    </button>
                    <button
                      type="button"
                      aria-label={`Move ${category.name} down`}
                      disabled={index === withCounts.length - 1}
                      onClick={() => void handleMove(index, 1)}
                      className="h-12 w-12 rounded-lg bg-aqua text-teal-dark font-heading font-bold disabled:opacity-30"
                    >
                      &darr;
                    </button>
                    <button
                      type="button"
                      onClick={() => startEdit(category)}
                      className="min-h-12 px-3 rounded-lg font-heading font-semibold text-base text-teal-dark"
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingDelete(category)}
                      className="min-h-12 px-3 rounded-lg font-heading font-semibold text-base text-danger"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={pendingDelete !== null}
        title={`Delete ${pendingDelete?.name ?? 'this category'}?`}
        description="Its products become uncategorised - they are not deleted."
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
        onConfirm={() => void handleDelete()}
        onCancel={() => setPendingDelete(null)}
      />
    </Page>
  );
}

function ColourPicker({ value, onChange }: { value: string | null; onChange: (v: string | null) => void }) {
  return (
    <div className="flex items-center gap-2" role="radiogroup" aria-label="Category colour">
      <button
        type="button"
        role="radio"
        aria-checked={value === null}
        aria-label="No colour"
        onClick={() => onChange(null)}
        className={clsx(
          'h-12 w-12 rounded-full border-2 flex items-center justify-center text-muted',
          value === null ? 'border-teal-dark' : 'border-border',
        )}
      >
        &times;
      </button>
      {BRAND_COLOURS.map((c) => (
        <button
          key={c.value}
          type="button"
          role="radio"
          aria-checked={value === c.value}
          aria-label={c.label}
          onClick={() => onChange(c.value)}
          className={clsx('h-12 w-12 rounded-full border-2', value === c.value ? 'border-teal-dark' : 'border-transparent')}
          style={{ backgroundColor: c.value }}
        />
      ))}
    </div>
  );
}
